import { Navigate, useParams } from "react-router-dom";
import { Loader } from "../components";
import { useGameweekContext } from "../data";
import { Matchup, MatchupEntry, PlayerPick } from "../models";
import { formatKickoff, matchupStatus, nextKickoff, sideProgress } from "../scoring";
import { ChipBadge, LiveBadge, OriginHint, PageHeader, PlayerRow, WinProbability } from "../ui";
import "./MatchupDetailScreen.css";

const resultClass = (side: MatchupEntry) => (side.result === "win" || side.result === "loss" ? side.result : "");

const splitLineup = (players: PlayerPick[]) => ({
	starters: players.filter((p) => p.active === 1),
	bench: players.filter((p) => p.active !== 1),
});

/** Both lineups as paired rows, so each player sits level with their opposite number. */
const PairedLineups = ({ home, away }: { home: MatchupEntry; away: MatchupEntry }) => {
	const h = splitLineup(home.players);
	const a = splitLineup(away.players);

	const pairs = (left: PlayerPick[], right: PlayerPick[]) =>
		Array.from({ length: Math.max(left.length, right.length) }, (_, i) => (
			<div key={left[i]?.id ?? `l${i}`} className="lineup-pair">
				<div className="lineup-cell">{left[i] && <PlayerRow player={left[i]} />}</div>
				<div className="lineup-cell">{right[i] && <PlayerRow player={right[i]} reversed />}</div>
			</div>
		));

	return (
		<div className="lineups">
			<div className="lineup-headings">
				<div className="eyebrow lineup-heading">
					Starting {h.starters.length} · {home.team}
				</div>
				<div className="eyebrow lineup-heading right">
					Starting {a.starters.length} · {away.team}
				</div>
			</div>
			<div className="card lineup-list starters">{pairs(h.starters, a.starters)}</div>
			{(h.bench.length > 0 || a.bench.length > 0) && (
				<>
					<div className="lineup-headings">
						<div className="eyebrow lineup-heading">Bench</div>
						<div className="eyebrow lineup-heading right">Bench</div>
					</div>
					<div className="card lineup-list bench">{pairs(h.bench, a.bench)}</div>
				</>
			)}
		</div>
	);
};

const Hero = ({ m }: { m: Matchup }) => {
	const status = matchupStatus(m);
	const home = sideProgress(m.home.players);
	const away = sideProgress(m.away.players);
	const statusLabel = status === "upcoming" ? `Kicks off ${formatKickoff(nextKickoff(m))}` : status === "done" ? "Full time" : "In play";
	const progress = status === "upcoming" ? "" : ` · ${home.played + home.inProgress}/${home.total} · ${away.played + away.inProgress}/${away.total}`;

	return (
		<div className="detail-hero">
			<div className="detail-hero-status">
				{statusLabel}
				{progress}
			</div>
			<div className="detail-hero-scoreline">
				<div className="detail-hero-side">
					<div className="detail-hero-team">{m.home.team}</div>
					<div className="detail-hero-manager">{m.home.name}</div>
					<ChipBadge chip={m.home.chip} hit={m.home.basePoints} inverted />
				</div>
				<div className="detail-hero-score mono">
					<span className={resultClass(m.home)}>{m.home.totalPoints}</span>
					<span className="detail-hero-sep">·</span>
					<span className={resultClass(m.away)}>{m.away.totalPoints}</span>
				</div>
				<div className="detail-hero-side right">
					<div className="detail-hero-team">{m.away.team}</div>
					<div className="detail-hero-manager">{m.away.name}</div>
					<ChipBadge chip={m.away.chip} hit={m.away.basePoints} inverted right />
				</div>
			</div>
			{m.prediction && (
				<div className="detail-hero-prediction">
					<WinProbability prediction={m.prediction} inverted />
					{status === "live" && (
						<div className="detail-hero-projected mono">
							<span>
								PROJ&nbsp;&nbsp;<strong>{m.prediction.projectedHome}</strong>
							</span>
							<span>90′ FINISH</span>
							<span>
								<strong>{m.prediction.projectedAway}</strong>&nbsp;&nbsp;PROJ
							</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export const MatchupDetailScreen = () => {
	const { id } = useParams();
	const { week, matchups, loading, loadingMessage, error, gameweekLive, matchesLive, secondsToNextFetch, matchesInPlay } = useGameweekContext();
	const matchup = matchups.find((m) => String(m.id) === id);

	if (!loading && !error && matchups.length > 0 && matchup == null) {
		return <Navigate to="/matchups" replace />;
	}

	return (
		<>
			<PageHeader
				title={week ? `Gameweek ${week.id}` : "Gameweek"}
				subtitle={matchup ? `${matchup.home.team} vs ${matchup.away.team}` : "Matchup"}
				backTo="/matchups"
				aside={gameweekLive && !loading ? <LiveBadge matchesLive={matchesInPlay} secondsToNextFetch={secondsToNextFetch} waiting={!matchesLive} /> : undefined}
			/>
			<Loader loading={loading} loadingMessage={loadingMessage} />
			{error && (
				<>
					<p className="error-message">{error}</p>
					<OriginHint />
				</>
			)}
			{matchup && (
				<>
					<Hero m={matchup} />
					<PairedLineups home={matchup.home} away={matchup.away} />
					<p className="detail-footnote faint">Win probability and projections are estimates from FPL expected points for the players still to play.</p>
				</>
			)}
		</>
	);
};
